// components/portal/SocketStatusIndicator.tsx
'use client';

import { memo, useCallback } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { copyTextToClipboard } from '#lib/utils';

export const SocketStatusIndicator = memo(() => {
  const { kernel } = useKernel();
  // socketStatus is the ONLY subscription: it is the only field this dot renders.
  // socketReconnectAttempts / socketLastEventTimestamp / userContext are read at click
  // time instead — subscribing to them re-rendered the indicator on every socket event
  // (MAINT_02 §B: callback-only props) and made the callback unstable on each one
  // (MAINT_04 §B), for a diagnostics string nobody reads until they click.
  const socketStatus = useKernelStore((state) => state.socketStatus);

  const handleCopyDiagnostics = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const state = kernel.current?.getState();
    if (!state) return;
    const { socketStatus: status, socketReconnectAttempts, socketLastEventTimestamp, userContext } = state;

    const report = `
--- Fater Socket Diagnostic Report ---
Timestamp: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
Socket Status: ${status}
Reconnect Attempts: ${socketReconnectAttempts}
Last Event Received: ${socketLastEventTimestamp || 'Never'}
User ID: ${userContext.dbUserId || 'None'}
Org ID: ${userContext.organisationId || 'None'}
Is Impersonating: ${userContext.isImpersonating}
--------------------------------------
    `.trim();

    void copyTextToClipboard(report)
      .then(() => toast.success('Diagnostic report copied to clipboard!'))
      .catch(() => toast.error('Failed to copy report.'));
  }, [kernel]);

  let bgColor = 'bg-gray-500';
  let isPulsing = false;

  if (socketStatus === 'connected') {
    bgColor = 'bg-emerald-500';
  } else if (socketStatus === 'connecting' || socketStatus === 'reconnecting') {
    bgColor = 'bg-amber-500';
    isPulsing = true;
  } else if (socketStatus === 'disconnected' || socketStatus === 'error') {
    bgColor = 'bg-red-500';
  }

  return (
    <div
      onClick={handleCopyDiagnostics}
      className="absolute -bottom-0.5 -right-0.5 z-50 flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded-full bg-[#1A1A1A] p-[2px] transition-transform hover:scale-125"
      title={`Socket: ${socketStatus}. Click to copy diagnostics.`}
    >
      <div className={clsx('h-full w-full rounded-full', bgColor, isPulsing && 'animate-pulse')} />
    </div>
  );
});
SocketStatusIndicator.displayName = 'SocketStatusIndicator';
